'use client'

import { useEffect, useState } from 'react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { Loading } from '../pages/loading'

export interface UseGuardTimeoutOptions {
  /** ms of continuous loading before the soft "tunggu dulu" message appears. Default 10_000 (10s). */
  waitMs?: number
  /** ms of continuous loading before the hard timeout/error state triggers. Default 180_000 (3min). */
  timeoutMs?: number
}

type GuardPhase = 'idle' | 'wait' | 'timeout'

/**
 * Arms two timers while `isLoading` is true:
 * - after `waitMs` → `showWait` (soft reassurance, keep waiting)
 * - after `timeoutMs` → `timedOut` (hard error, give up)
 *
 * Resets to idle the moment loading completes. Pass the combined loading signal
 * (session + org query) so a slow API call on either is covered.
 */
export function useGuardTimeout(
  isLoading: boolean,
  { waitMs = 10_000, timeoutMs = 180_000 }: UseGuardTimeoutOptions = {},
) {
  const [phase, setPhase] = useState<GuardPhase>('idle')

  useEffect(() => {
    if (!isLoading) {
      setPhase((prev) => (prev === 'idle' ? prev : 'idle'))
      return
    }

    const waitId = setTimeout(() => setPhase('wait'), waitMs)
    const timeoutId = setTimeout(() => setPhase('timeout'), timeoutMs)

    return () => {
      clearTimeout(waitId)
      clearTimeout(timeoutId)
    }
  }, [isLoading, waitMs, timeoutMs])

  return { showWait: phase === 'wait', timedOut: phase === 'timeout' }
}

export interface AuthGuardWaitingProps {
  className?: string
  title?: string
  description?: string
}

/** Soft "still loading, hang tight" screen. Reuses the {@link Loading} visual with a reassurance message. */
export function AuthGuardWaiting({
  className,
  title = 'Tunggu sebentar ya...',
  description = 'Sedang memuat, koneksi mungkin sedang lambat.',
}: AuthGuardWaitingProps) {
  return (
    <div className={cn('srd:relative srd:h-dvh srd:w-full', className)}>
      <Loading />
      <div className='srd:absolute srd:inset-x-0 srd:bottom-16 srd:z-20 srd:flex srd:flex-col srd:items-center srd:gap-1 srd:px-6 srd:text-center'>
        <p className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-navy-100')}>{title}</p>
        <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-navy-60')}>{description}</p>
      </div>
    </div>
  )
}

export type AuthGuardErrorReason = 'timeout' | 'org'

export interface AuthGuardErrorProps {
  reason: AuthGuardErrorReason
  /** Destination for the "Ke halaman masuk" link. */
  loginHref: string
  className?: string
}

const ERROR_MESSAGES: Record<AuthGuardErrorReason, string> = {
  timeout: 'Sesi tidak dapat dimuat. Periksa koneksi atau coba lagi nanti.',
  org: 'Organisasi tidak dapat dimuat. Silahkan masuk kembali atau hubungi bantuan.',
}

/** Hard failure screen shown once loading has truly given up (timeout) or org resolution is exhausted. */
export function AuthGuardError({ reason, loginHref, className }: AuthGuardErrorProps) {
  return (
    <div
      className={cn(
        'srd:flex srd:min-h-dvh srd:flex-col srd:items-center srd:justify-center srd:gap-4 srd:p-4',
        className,
      )}
    >
      <p className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-navy-100 srd:text-center')}>
        {ERROR_MESSAGES[reason]}
      </p>
      <a
        href={loginHref}
        className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-main-blue-100 srd:hover:underline')}
      >
        Ke halaman masuk
      </a>
    </div>
  )
}
